import { Chart, registerables } from "chart.js";
import "chartjs-adapter-date-fns";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { message } from "antd";
import ChartOptions from "./ChartOptions";
import axios from "axios";
import MultiSlider from "./MultiSlider";
import Bridge from "../../utils/Bridge";
import URLs from "../../utils/Urls";

// Register Chart.js components
Chart.register(...registerables);

const NUMBER_OF_CHARTS = 2;
const NOTE_DATE_FORMAT = "MM/DD/YY";

const plugin = {
  beforeDraw: (chart) => {
    const ctx = chart.canvas.getContext("2d");
    var chartArea = chart.chartArea;
    ctx.save();
    ctx.fillStyle = "#FAFCFC";
    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.restore();
  },
};

const DEFAULT_DATASET = [
  {
    label: "",
    data: [],
    fill: false,
    backgroundColor: "#0057F0",
    borderColor: "#0057F0",
    yAxisID: "y",
    pointStyle: "rect",
    pointBackgroundColor: "#FFFFFF",
    pointRadius: 8,
  },
  {
    label: "",
    data: [],
    fill: false,
    backgroundColor: "#F22432",
    borderColor: "#F22432",
    yAxisID: "y1",
    pointStyle: "triangle",
    pointBackgroundColor: "#FFFFFF",
    pointRadius: 8,
    pointBorderColor: "#F22432",
  },
];

const Charts = ({ client_code, sessionData }) => {
  const [selectedStat, setSelectedStat] = useState([null, null]);
  const [statData, setStatData] = useState([[], []]);
  const [startDate, setStartDate] = useState(sessionData?.FIRST_SESSION_DATE);
  const [endDate, setEndDate] = useState(sessionData?.LAST_SESSION_DATE);
  const [notesData, setNotesData] = useState([]);
  useEffect(() => {
    setStartDate(sessionData?.FIRST_SESSION_DATE);
    setEndDate(sessionData?.LAST_SESSION_DATE);
  }, [sessionData]);
  const filterNotes = (x) => {
    return notesData?.data?.results.filter((note) => {
      // Check if note has end date or not
      let noteStart = moment
        .utc(note.STARTTIME)
        .format(NOTE_DATE_FORMAT)
        .valueOf();
      let xValue = moment.utc(x).format(NOTE_DATE_FORMAT).valueOf();
      let noteEnd = null;
      if (note.ENDTIME && note.ENDTIME !== "") {
        noteEnd = moment.utc(note.ENDTIME).format(NOTE_DATE_FORMAT).valueOf();
      }
      if (!noteEnd && noteStart <= xValue) {
        return true;
      }
      if (noteEnd && xValue >= noteStart && xValue <= noteEnd) {
        return true;
      }
      return false;
    });
  };
  const handleSliderDateChange = (data = []) => {
    setStartDate(data[0]);
    setEndDate(data[1]);
  };
  const graphClickEvent = (event, array) => {
    setShowInterventionNotes(true);
  };
  const options = {
    onClick: graphClickEvent,
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: false,
    plugins: {
      legend: false,
      tooltip: {
        backgroundColor: "#FFFFFF",
        titleColor: "#3F4255",
        titleFont: { family: "lato", size: 15, weight: 500 },
        bodyColor: "#3F4255",
        bodyFont: { family: "lato", size: 15, weight: 500 },
        footerColor: "blue",
        footerFont: { family: "lato", size: 15, weight: 500 },
        afterFooterColor: "blue",
        cornerRadius: 5,
        displayColors: false,
        callbacks: {
          title: function (context) {
            return "";
          },
          label: function (context) {
            let label = `${context.dataset.label}: ${context.formattedValue}`;
            return label;
          },
          footer: function (context) {
            if (context && context.length > 0) {
              if (context[0].label) {
                return `Date: ${moment(context[0].raw.x).format("MM/DD/YY")}`;
              }
              return "";
            }
            return "";
          },
          afterFooter: function (context) {
            let notes = [];
            if (
              notesData &&
              notesData.data &&
              notesData.data.results.length > 0
            ) {
              if (context && context.length > 0) {
                if (context[0].label) {
                  const notesdetail = filterNotes(context[0].raw.x);

                  notesdetail.map((note) => notes.push(note.TITLE));
                  if (notes && notes.length > 0) {
                    let finalNotes = notes.join(",");
                    if (finalNotes.length > 36) {
                      return `Notes: ${finalNotes.substring(0, 33)}...`;
                    }
                    return `Notes: ${finalNotes}`;
                  }
                  return "";
                }
              }
            }

            return "";
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Date",
          color: "#363547",
          font: {
            family: "Lato",
            size: 18,
            style: "normal",
            lineHeight: "22px",
          },
        },
        type: "time",
        distribution: "series",
        time: {
          unit: "day",
          displayFormats: {
            day: "MM/dd/yy",
          },
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "ALSFRS",
          color: "#0057F0",
          font: {
            family: "Lato",
            size: 20,
            style: "normal",
            lineHeight: 1.2,
          },
          padding: { top: 30, left: 0, right: 0, bottom: 0 },
        },
        // grid line settings
        grid: {
          drawOnChartArea: false, // only want the grid lines for one axis to show up
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: true,
          text: "Words Per Minute",
          color: "#F22432",
          font: {
            family: "Lato",
            size: 20,
            style: "normal",
            lineHeight: 1.2,
          },
          padding: { top: 30, left: 0, right: 0, bottom: 0 },
        },
        // grid line settings
        grid: {
          drawOnChartArea: false, // only want the grid lines for one axis to show up
        },
      },
    },
  };
  const [chartOptions, setChartOptions] = useState({ ...options });
  const [data, setData] = useState({
    labels: [],
    datasets: [],
  });
  const [showInterventionNotes, setShowInterventionNotes] = useState(false);

  const fetchChartOne = (value, category_value) => {
    let cate_text = category_value ? `&categories=${category_value}` : "";
    let params = `/${client_code}?stat=charts&chartparams=${value}&startdate=${startDate}&enddate=${endDate}${cate_text}`;
    Bridge.personal_data_viewer.get_chart_one_data(params).then((res) => {
      statData[0] = res.data?.results || [];
      setStatData([...statData]);
      setTimeout(() => {
        getChartData(0);
      }, 2000);
    });
  };
  const fetchChartTwo = (value, category_value) => {
    let cate_text = category_value ? `&categories=${category_value}` : "";
    let params = `/${client_code}?stat=charts&chartparams=${value}&startdate=${startDate}&enddate=${endDate}${cate_text}`;
    Bridge.personal_data_viewer.get_chart_two_data(params).then((res) => {
      statData[1] = res.data?.results || [];
      setStatData([...statData]);
      setTimeout(() => {
        getChartData(1);
      }, 2000);
    });
  };
  const getChartData = (index) => {
    // let url = `${process.env.REACT_APP_PATIENT_PORTAL_BASE_URL}report/${client_code}?stat=charts&chartparams=${selectedStat[index]?.value}&startdate=${startDate}&enddate=${endDate}`;
    // axios.get(url).then((res) => {

    // });
    let queryParams = {
      stat: "charts",
      chartparams: selectedStat[index]?.value,
      startdate: startDate,
      enddate: endDate,
    };
    if (selectedStat[index]?.catValue) {
      queryParams.categories = selectedStat[index]?.catValue;
    }
    return listApiHandler({
      client_code,
      queryParams,
    });
  };
  const listApiHandler = async (params) => {
    let url = `${URLs.get_report_data}/${params.client_code}`;
    if (params.queryParams) {
      let qp = [];
      for (let key in params.queryParams) {
        qp.push(`${key}=${params.queryParams[key]}`);
      }
      url = url + `?${qp.join("&")}`;
    }
    let response = await fetch(url);
    return response.json();
  };
  useEffect(() => {
    function load() {
      if (selectedStat.length > 0) {
        fetchChartOne(
          "alsfrsr",
          selectedStat[0]?.catValue ? selectedStat[0]?.catValue : null
        );
        fetchChartTwo(
          "wpm",
          selectedStat[1]?.catValue ? selectedStat[1]?.catValue : null
        );
        setChartOptions({ ...options });
      }
    }
    load();
  }, [startDate, endDate]);
  useEffect(() => {
    function load() {
      if (selectedStat.length > 0) {
        fetchChartOne(
          selectedStat[0]?.value,
          selectedStat[0]?.catValue || null
        );
        fetchChartTwo(
          selectedStat[1]?.value,
          selectedStat[1]?.catValue || null
        );
        setChartOptions({ ...options });
      } else {
        message.warning("Choose chart options");
      }
    }
    load();
  }, [selectedStat]);

  const validateStats = () => {
    if (!selectedStat[0] && !selectedStat[1]) return true;
  };
  useEffect(() => {
    fetchNotesData();
  }, []);
  useEffect(() => {
    function load() {
      loadCharts();
    }
    load();
  }, [statData]);

  // This function is used to map data from api to chart data
  const loadCharts = () => {
    let labels = [];
    let datasets = [];
    if (statData && statData.length > 0) {
      let min = null,
        max = null;
      for (let stat of statData) {
        for (let result of stat) {
          if (!labels.includes(result.date)) {
            labels.push(result.date);
            if (!min) {
              min = result.date;
            } else if (moment(result.date).isBefore(moment(min))) {
              min = result.date;
            }
            if (!max) {
              max = result.date;
            } else if (moment(result.date).isAfter(moment(min))) {
              max = result.date;
            }
          }
        }
      }
      let newChartOptions = { ...chartOptions };
      newChartOptions.scales.x.time.min = min;
      newChartOptions.scales.x.time.max = max;
      newChartOptions.scales.y.title.text = selectedStat[0]?.title || "";
      newChartOptions.scales.y1.title.text = selectedStat[1]?.title || "";
      // Hide if only one chart is selected

      newChartOptions = hideAxis(newChartOptions);
      let index = 0;

      for (let stat of statData) {
        let ds = DEFAULT_DATASET[index % 2];
        let statDataset = [];
        let pointBackgroundColor = [];
        if (labels?.length > 0) {
          labels.sort(
            (a, b) => moment.utc(a).valueOf() - moment.utc(b).valueOf()
          );
        }
        for (let label of labels) {
          let scoreData = stat.find((s) => s.date === label);
          if (scoreData) {
            statDataset.push({
              x: scoreData.date,
              y: scoreData.score,
              extradata: scoreData.extradata,
            });
            let notes = filterNotes(moment.utc(label).format(NOTE_DATE_FORMAT));
            if (notes?.length > 0) {
              pointBackgroundColor.push(ds.backgroundColor);
            } else {
              pointBackgroundColor.push("#FFFFFF");
            }
          }
        }

        datasets.push({
          ...ds,
          label: selectedStat[index]?.legend,
          pointBackgroundColor,
          data: statDataset,
        });
        index++;
      }
      setChartOptions(newChartOptions);
    }

    for (var i = 0; i < datasets.length; i++) {
      if (i == 0) {
        for (let d of datasets[i].data) {
          let total = 0;
          let BULBAR = 0;
          let FINEMOTOR = 0;
          let GROSSMOTOR = 0;
          let RESPIRATORY = 0;
          let i = 0;
          if (d.extradata != undefined) {
            for (let inner of d.extradata) {
              total += Number(inner.questiongroup_score);
              if (i == "0") {
                BULBAR = inner.questiongroup_score;
              } else if (i == "1") {
                FINEMOTOR = inner.questiongroup_score;
              } else if (i == "2") {
                GROSSMOTOR = inner.questiongroup_score;
              } else if (i == "3") {
                RESPIRATORY = inner.questiongroup_score;
              }
              i++;
            }
            d.total_score = total;
            d.score = d.y;
            d.y = total;
            d.BULBAR = BULBAR;
            d.FINEMOTOR = FINEMOTOR;
            d.GROSSMOTOR = GROSSMOTOR;
            d.RESPIRATORY = RESPIRATORY;
          }
        }
      }
    }
    for (var i = 0; i < datasets.length; i++) {
      if (i == 0) {
        for (let d of datasets[i].data) {
          if (selectedStat[0] != null) {
            if (selectedStat[0].id == "BULBAR") {
              d.y = d.BULBAR;
            }
            if (selectedStat[0].id == "FINEMOTOR") {
              d.y = d.FINEMOTOR;
            }
            if (selectedStat[0].id == "GROSSMOTOR") {
              d.y = d.GROSSMOTOR;
            }
            if (selectedStat[0].id == "RESPIRATORY") {
              d.y = d.RESPIRATORY;
            }
          }
        }
      }
    }
    setData({ ...data, labels, datasets });
  };
  //Hide/Unhide axis
  const hideAxis = (options) => {
    // There are 2 axis based on group index hide/unhide axis
    options.scales.y1.display = true;
    options.scales.y.display = true;
    options.scales.y1.position = "right";
    options.scales.y.position = "left";

    for (let i = 0; i < selectedStat.length; i++) {
      if (!selectedStat[i]) {
        if (i === 0) {
          options.scales.y.display = false;
          options.scales.y1.position = "left";
        } else {
          options.scales.y1.display = false;
          options.scales.y.position = "left";
        }
      }
    }
    return options;
  };
  const renderCharts = () => {
    const val = validateStats();
    if (data) {
      if (data.labels.length == 0 || data.datasets.length == 0) {
        return;
      }
    }
    if (!val && data.labels.length > 0 && data.datasets.length > 0) {
      return <Line data={data} options={chartOptions} plugins={[plugin]} />;
    } else {
      return (
        <div style={{ minHeight: 300 }}>
          <p>Not available.</p>
        </div>
      );
    }
    if (!data) {
      <div style={{ minHeight: 300 }}>
        <p>Not available.</p>
      </div>;
    }
  };
  const fetchNotesData = () => {
    let params = `/${client_code}?startdate=${startDate}&enddate=${endDate}`;
    Bridge.personal_data_viewer.get_notes_data(params).then((res) => {
      setNotesData(res);
    });
  };
  const renderUserTips = () => {
    return (
      <div className="row">
        <div className="col-lg-12">
          <div className="chart_datafooter">
            <h4>User Tips:</h4>
            <ul>
              <li className="d-flex">
                <span>1.</span>
                <p>
                  Hover your mouse over the charts data points markers to see
                  the value for that entry.
                </p>
              </li>
              <li className="d-flex">
                <span>2.</span>
                <p>
                  {" "}
                  Adjust the start and stop sliders below the graph to see a
                  specific date range.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  };
  const renderSlider = () => {
    return (
      <MultiSlider
        options={{
          edgeTicks: {
            type: "date",
            displayFormat: "MMM DD, YY",
            start: sessionData?.FIRST_SESSION_DATE,
            end: sessionData?.LAST_SESSION_DATE,
          },
        }}
        onChange={handleSliderDateChange}
      ></MultiSlider>
    );
  };
  const renderSelectChart = () => {
    return (
      <>
        <ChartOptions
          onSelect={onSelect}
          client_code={client_code}
          onDefaultSelection={onDefaultSelection}
        ></ChartOptions>
      </>
    );
  };
  const onSelect = (events) => {
    let newSelectedStat = [...selectedStat];
    for (let e of events) {
      if (e.state.selected) {
        let foundNullNode = false;
        for (let i = 0; i < newSelectedStat.length; i++) {
          if (newSelectedStat[i] === null) {
            newSelectedStat[i] = { ...e.customData };
            foundNullNode = true;
            break;
          }
        }
        if (!foundNullNode) {
          newSelectedStat.push({ ...e.customData });
        }
      } else {
        for (let i = 0; i < newSelectedStat.length; i++) {
          if (
            newSelectedStat[i] !== null &&
            newSelectedStat[i].id === e.customData.id
          ) {
            // If we are unselecting > nUmber of charts then we simply pop
            newSelectedStat[i] = null;
            if (i >= NUMBER_OF_CHARTS) {
              newSelectedStat.splice(i, 1);
            }
          }
        }
        if (newSelectedStat.length > NUMBER_OF_CHARTS) {
          for (let i = 0; i < NUMBER_OF_CHARTS; i++) {
            // check if any of stat is null
            if (newSelectedStat[i] === null) {
              // Copy the 3rd row here
              newSelectedStat[i] = newSelectedStat.pop();
            }
          }
        }
      }
    }
    setSelectedStat(newSelectedStat);
  };
  const onDefaultSelection = (stats) => {
    setSelectedStat(stats);
  };
  const notAvailableStyles = {
    fontSize: 19,
    textAlign: "center",
    padding: "16px 0px 0px",
  };
  return (
    <div>
      {statData && statData.length > 0 && (
        <div className="row">
          <div className="col-lg-8">
            {data.labels.length > 0 &&
              data.datasets.length > 0 &&
              renderCharts()}
            {sessionData && renderSlider()}
          </div>
          <div className={`pl-0 col-lg-4`}>
            {client_code && renderSelectChart()}
          </div>
        </div>
      )}
      {statData && statData[0].length > 0 ? (
        renderUserTips()
      ) : (
        <p className="text-center" style={notAvailableStyles}>
          Not available.
        </p>
      )}
      {/* {showInterventionNotes && (
        <InterventionNotes
          showModal={showInterventionNotes}
          onClose={() => setShowInterventionNotes(false)}
          clientCode={client_code}
          notesData={notesData?.data || []}
          onEdit={setEditInfo}
        ></InterventionNotes>
      )} */}
    </div>
  );
};
export default Charts;
